import React, { useState, useMemo } from 'react';
import Button from '../../components/buttons/Button';
import { formatDisplayName } from '../../models/ReportTypeDesignHelper';
import { isCapacitor } from '../../utils/platform';
import {
    FaTrash,
    FaTimes,
    FaFile,
    FaFileAlt,
    FaCamera,
    FaPaperPlane,
    FaChevronDown,
    FaChevronUp,
    FaPaperclip,
    FaBroom,
    FaHistory,
    FaCircle,
    FaPlane,
    FaMicrophone,
    FaImage
} from 'react-icons/fa';
import './DraftsPanel.css';

export default function DraftsPanel({
    activeDrafts = [],
    sentDrafts = [],
    deletedDrafts = [],
    liveDrafts = [],
    airplaneItems = [],
    onSelectDraft,
    onDeleteDraft,
    onDeleteAllDraft,
    onBulkClearSection,
    onAirplaneItemTap,
    onClearAirplaneItems,
    onClose,
    formatDate
}) {
    // Debug: log props on render
    console.log('[DraftsPanel] Render - airplaneItems:', airplaneItems.length, 'onAirplaneItemTap exists:', !!onAirplaneItemTap);
    if (airplaneItems.length > 0) {
        console.log('[DraftsPanel] First airplane item:', JSON.stringify(airplaneItems[0]));
    }

    const [showActive, setShowActive] = useState(false);
    const [showSent, setShowSent] = useState(false);
    const [showDeleted, setShowDeleted] = useState(false);
    const [showLive, setShowLive] = useState(false);
    const [showAirplane, setShowAirplane] = useState(airplaneItems.length > 0);

    // Helper function to handle opening one section at a time
    const handleSectionToggle = (section) => {
        if (section === 'airplane') {
            setShowAirplane(!showAirplane);
            setShowActive(false);
            setShowSent(false);
            setShowDeleted(false);
            setShowLive(false);
        } else if (section === 'active') {
            setShowActive(!showActive);
            setShowAirplane(false);
            setShowSent(false);
            setShowDeleted(false);
            setShowLive(false);
        } else if (section === 'sent') {
            setShowSent(!showSent);
            setShowAirplane(false);
            setShowActive(false);
            setShowDeleted(false);
            setShowLive(false);
        } else if (section === 'deleted') {
            setShowDeleted(!showDeleted);
            setShowAirplane(false);
            setShowActive(false);
            setShowSent(false);
            setShowLive(false);
        } else if (section === 'live') {
            setShowLive(!showLive);
            setShowAirplane(false);
            setShowActive(false);
            setShowSent(false);
            setShowDeleted(false);
        }
    };

    const sortByDate = (drafts) => {
        return [...drafts].sort((a, b) => {
            // Use date_created for sorting (it's already a Date object from the Draft class)
            const dateA = a.date_created;
            const dateB = b.date_created;
            return dateB - dateA;
        });
    };

    const sortedActiveDrafts = useMemo(() => sortByDate(activeDrafts), [activeDrafts]);
    const sortedSentDrafts = useMemo(() => sortByDate(sentDrafts), [sentDrafts]);
    const sortedDeletedDrafts = useMemo(() => sortByDate(deletedDrafts), [deletedDrafts]);
    const sortedLiveDrafts = useMemo(() => sortByDate(liveDrafts), [liveDrafts]);

    const handleSelectDraftAndClose = (draft, action) => {
        onSelectDraft(draft, action);
        onClose();
    };

    const handleClearSection = (section) => {
        if (onBulkClearSection) {
            onBulkClearSection(section);
            return;
        }
        // Fallback: previous behavior deleting one by one
        if (section === 'active') {
            onDeleteAllDraft(sortedActiveDrafts.map(draft => draft.id), false);
        } else if (section === 'sent') {
            onDeleteAllDraft(sortedSentDrafts.map(draft => draft.id), false);
        } else if (section === 'deleted') {
            onDeleteAllDraft(sortedDeletedDrafts.map(draft => draft.id), true);
        } else if (section === 'live') {
            onDeleteAllDraft(sortedLiveDrafts.map(draft => draft.id), true);
        }
    };

    const DraftItem = ({ draft, showFullButtons = false, isPermanentDelete = false }) => (
        <div
            className="draft-item"
            onClick={() => handleSelectDraftAndClose(draft, 'replaceAll')}
            title="Cliquer pour charger cette dictée complète"
        >
            <div className="draft-item-header">
                <div className="draft-item-meta">
                    <strong className="draft-report-type">
                        {formatDisplayName(draft.report_type) || 'Texte'}
                    </strong>
                    <span className="draft-date">
                        {formatDate(draft.date_created)}
                    </span>
                </div>
            </div>
            
            <div className="draft-text-preview">
                {draft.content && draft.content.length > 60 
                    ? draft.content.slice(0, 60) + '...' 
                    : draft.content || '(Vide)'}
            </div>
            
            {draft.annex && draft.annex.length > 0 && (
                <div className="draft-annex-indicator">
                    <FaPaperclip size={12} />
                    <span>Annexe</span>
                </div>
            )}
            
            {showFullButtons && (
                <div className="draft-item-buttons">
                    <div className="draft-item-buttons-group">
                        <Button
                            type="inversed"
                            icon={FaFileAlt}
                            variant="primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSelectDraftAndClose(draft, 'replaceText');
                            }}
                            title="Charger seulement le texte de cette dictée"
                        >
                            <span className="draft-button-text">Texte</span>
                        </Button>
                        {draft.annex && draft.annex.length > 0 && (
                            <Button
                                type="inversed"
                                icon={FaCamera}
                                variant="camera"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectDraftAndClose(draft, 'replaceAnnex');
                                }}
                                title="Charger seulement l'annexe de cette dictée"
                            >
                                <span className="draft-button-text">Annexe</span>
                            </Button>
                        )}
                    </div>
                    <Button
                        type="inversed"
                        icon={FaTrash}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDeleteDraft(draft.id, isPermanentDelete);
                        }}
                        title={isPermanentDelete ? "Supprimer définitivement" : "Supprimer cette dictée"}
                    >
                    </Button>
                </div>
            )}
        </div>
    );

    // Component for rendering airplane mode items (audio, OCR, or text)
    const AirplaneItemComponent = ({ item }) => {
        const isAudio = item.type === 'audio';
        const isText = item.type === 'text';
        const formattedDate = item.createdAt
            ? new Date(item.createdAt).toLocaleString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            })
            : '';

        const formatDuration = (seconds) => {
            const mins = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${mins}:${secs.toString().padStart(2, '0')}`;
        };

        // Get preview text for text items
        const getTextPreview = () => {
            const text = item.transcript || item.annex || '';
            return text.length > 40 ? text.substring(0, 40) + '...' : text;
        };

        // Get title based on type
        const getTitle = () => {
            if (isAudio) return "Cliquer pour transcrire cet audio";
            if (isText) return "Cliquer pour charger ce brouillon";
            return "Cliquer pour extraire le texte de ce fichier";
        };

        // Get icon based on type
        const getIcon = () => {
            if (isAudio) return <FaMicrophone className="airplane-icon-audio" size={20} />;
            if (isText) return <FaFileAlt className="draft-icon-blue" size={20} />;
            return <FaImage className="airplane-icon-ocr" size={20} />;
        };

        // Get description based on type
        const getDescription = () => {
            if (isAudio) {
                return <span>Audio <strong>{formatDuration(item.durationSeconds || 0)}</strong></span>;
            }
            if (isText) {
                return <span className="airplane-item-filename">{getTextPreview()}</span>;
            }
            return <span className="airplane-item-filename">{item.fileName || 'Fichier'}</span>;
        };

        return (
            <div
                className="airplane-item"
                onClick={(e) => {
                    e.stopPropagation();
                    console.log('[DraftsPanel] Airplane item clicked:', item.id, 'callback exists:', !!onAirplaneItemTap);
                    if (onAirplaneItemTap) {
                        onAirplaneItemTap(item);
                    }
                }}
                title={getTitle()}
            >
                <div className="airplane-item-icon">
                    {getIcon()}
                </div>
                <div className="airplane-item-info">
                    <div className="airplane-item-type">
                        {getDescription()}
                    </div>
                    <div className="airplane-item-date">
                        {formattedDate}
                    </div>
                </div>
                <div className="airplane-item-action">
                    <span className="airplane-tap-hint">{isText ? 'Toucher pour charger' : 'Toucher pour traiter'}</span>
                </div>
            </div>
        );
    };

    const CollapsibleSection = ({ title, icon, count, isOpen, onToggle, onClear, children, clearIcon = FaBroom }) => (
        <div className="draft-collapsible-section">
            <div
                className="draft-section-header"
                onClick={onToggle}
                title={`Cliquer pour ${isOpen ? 'fermer' : 'ouvrir'} la section ${title}`}
            >
                <div className="draft-section-header-left">
                    {React.cloneElement(icon, { className: icon.props.className })}
                    <span className="draft-section-title">{title}</span>
                    <span className="draft-section-count">{count}</span>
                </div>
                <div className="draft-section-header-right">
                    {count > 0 && (
                        <Button
                            type="inversed"
                            variant="danger"
                            icon={clearIcon}
                            onClick={(e) => {
                                e.stopPropagation();
                                onClear();
                            }}
                            title={`Supprimer toutes les dictées de la section ${title}`}
                        >
                            <span className="draft-button-text">Vider</span>
                        </Button>
                    )}
                    {isOpen ? <FaChevronUp size={16} /> : <FaChevronDown size={16} />}
                </div>
            </div>
            {isOpen && (
                <div className="draft-collapsible-content">
                    {children}
                </div>
            )}
        </div>
    );

    return (
        <div className="draft-panel">
            <div className="draft-panel-header">
                <div className="draft-panel-header-content">
                    <h3 className="draft-panel-title">
                        <span>Historique</span>
                    </h3>
                    <Button
                        icon={FaTimes}
                        variant="secondary"
                        onClick={onClose}
                        title="Fermer"
                    />
                </div>
                <p className="draft-panel-description">
                    {isCapacitor
                        ? "Touchez un brouillon pour le charger dans le dictaphone, ou seulement sur Texte ou Annexe pour ne récupérer qu'eux"
                        : "Cliquez sur un brouillon pour le charger dans le dictaphone, ou seulement sur Texte ou Annexe pour ne récupérer que le texte ou l'annexe"}
                </p>
            </div>

            <div className="draft-panel-scrollable">
                {/* Section Mode Avion - Affichée en premier si des items existent */}
                {airplaneItems.length > 0 && (
                    <CollapsibleSection
                        title="Mode Avion"
                        icon={<FaPlane className="draft-icon-orange" size={16} />}
                        count={airplaneItems.length}
                        isOpen={showAirplane}
                        onToggle={() => handleSectionToggle('airplane')}
                        onClear={() => onClearAirplaneItems && onClearAirplaneItems()}
                    >
                        <p className="airplane-section-hint">
                            Touchez un élément pour le transcrire immédiatement
                        </p>
                        {airplaneItems.map((item) => (
                            <AirplaneItemComponent key={item.id} item={item} />
                        ))}
                    </CollapsibleSection>
                )}

                <CollapsibleSection
                    title="Brouillons"
                    icon={<FaFile className="draft-icon-blue" size={16} />}
                    count={sortedActiveDrafts.length}
                    isOpen={showActive}
                    onToggle={() => handleSectionToggle('active')}
                    onClear={() => handleClearSection('active')}
                >
                    {sortedActiveDrafts.length === 0 ? (
                        <p className="draft-empty-state">Aucun brouillon</p>
                    ) : (
                        sortedActiveDrafts.map((draft) => (
                            <DraftItem key={draft.id} draft={draft} showFullButtons={true} />
                        ))
                    )}
                </CollapsibleSection>

                <CollapsibleSection
                    title="Dictées envoyées"
                    icon={<FaPaperPlane className="draft-icon-green" size={16} />}
                    count={sortedSentDrafts.length}
                    isOpen={showSent}
                    onToggle={() => handleSectionToggle('sent')}
                    onClear={() => handleClearSection('sent')}
                >
                    {sortedSentDrafts.length === 0 ? (
                        <p className="draft-empty-state">Aucune dictée envoyée</p>
                    ) : (
                        sortedSentDrafts.map((draft) => (
                            <DraftItem key={draft.id} draft={draft} showFullButtons={true} />
                        ))
                    )}
                </CollapsibleSection>

                <CollapsibleSection
                    title="Corbeille"
                    icon={<FaTrash className="draft-icon-red" size={16} />}
                    count={sortedDeletedDrafts.length}
                    isOpen={showDeleted}
                    onToggle={() => handleSectionToggle('deleted')}
                    onClear={() => handleClearSection('deleted')}
                    clearIcon={FaTrash}
                >
                    {sortedDeletedDrafts.length === 0 ? (
                        <p className="draft-empty-state">Corbeille vide</p>
                    ) : (
                        sortedDeletedDrafts.map((draft) => (
                            <DraftItem key={draft.id} draft={draft} showFullButtons={true} isPermanentDelete={true} />
                        ))
                    )}
                </CollapsibleSection>

                {sortedLiveDrafts.length > 0 && (
                    <CollapsibleSection
                        title="Dictées Live"
                        icon={<FaCircle className="draft-icon-orange" size={16} />}
                        count={sortedLiveDrafts.length}
                        isOpen={showLive}
                        onToggle={() => handleSectionToggle('live')}
                        onClear={() => handleClearSection('live')}
                        clearIcon={FaTrash}
                    >
                        {sortedLiveDrafts.length === 0 ? (
                            <p className="draft-empty-state">Aucune dictée en direct</p>
                        ) : (
                            sortedLiveDrafts.map((draft) => (
                                <DraftItem key={draft.id} draft={draft} showFullButtons={true} isPermanentDelete={true} />
                            ))
                        )}
                    </CollapsibleSection>
                )}
            </div>
        </div>
    );
}